import {
  Mtds,
  EmptyState,
  TopBar,
  AppLayout,
  useGetContractsQuery,
  ContractAnalysisRow,
  CombinationsToggleValue,
  getCombinationStatus,
  ScreenHeader
} from 'prove-common'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBarLayout } from '../components/side-bar-layout/SideBarLayout'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router'
import { CombinationsTable } from './tables/CombinationsTable'
import type { PaginationState } from '@tanstack/react-table'
import { BASE_URL } from '../constants'

export const CombinationsScreen = () => {
  const { data: contracts, isPending, isError } = useGetContractsQuery()
  const [showCodes, setShowCodes] = useState(true)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20
  })
  const tableData: ContractAnalysisRow[] =
    (contracts ?? [])
      .filter((contract): contract is typeof contract & { etag: string } =>
        Boolean(contract.etag)
      )
      .flatMap((contract) =>
        (contract.analysisTypes ?? []).map((analysisType) => ({
          contractId: contract.id,
          contractEtag: contract.etag,
          analysisType,
          status: getCombinationStatus(analysisType)
        }))
      ) ?? []

  const counts = {
    awaiting: tableData.filter((d) => d.status === 'awaiting').length,
    approved: tableData.filter((d) => d.status === 'approved').length,
    declined: tableData.filter((d) => d.status === 'declined').length,
    all: tableData.length
  }
  const [searchParams, setSearchParams] = useSearchParams()
  const rawToggleParam = searchParams.get('toggle')
  const toggleValueParam: CombinationsToggleValue | null =
    rawToggleParam === 'awaiting' ||
    rawToggleParam === 'approved' ||
    rawToggleParam === 'declined' ||
    rawToggleParam === 'all'
      ? rawToggleParam
      : null
  // Initialize toggleValue from URL param or fallback to localStorage or 'all'
  const [toggleValue, setToggleValue] = useState<CombinationsToggleValue>(
    () =>
      toggleValueParam ??
      ((localStorage.getItem('localSettings.combinations.toggleValue') ??
        'all') as CombinationsToggleValue)
  )
  const items = [
    ['awaiting', `Awaiting approval (${counts.awaiting})`],
    ['approved', `Approved (${counts.approved})`],
    ['declined', `Declined (${counts.declined})`],
    ['all', `All (${counts.all})`]
  ] as const
  const selectedTableData =
    toggleValue === 'all'
      ? tableData
      : tableData.filter((row) => row.status === toggleValue)
  // Persist toggleValue to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('localSettings.combinations.toggleValue', toggleValue)
  }, [toggleValue])
  useEffect(() => {
    setSearchParams(
      (prev) => {
        prev.set('toggle', toggleValue.toString())
        return prev
      },
      { replace: true }
    )
  }, [toggleValue])

  return (
    <AppLayout
      sidebar={<SideBarLayout />}
      isLoading={isPending && !isError}
      isError={isError}
      isFullWidth={true}
      topBar={
        <TopBar
          logo={<AppLogo />}
          title='Combinations'
          showNotificationBell={true}
          isProvePlan={false}
        />
      }
    >
      <ScreenHeader
        title='Combinations'
        description={
          <p>
            A <strong>combination</strong> defines a single type of analysis the
            lab is contracted to perform. It specifies the sample type (product
            and matrix), the substances to be measured, and the schemas that
            describe the data included in each requisition. Before Mattilsynet
            can send requisitions for a combination, the lab must approve it.
          </p>
        }
      />
      <Mtds.Alert data-color='info'>
        <p>
          For authentication, environments, the full requisition workflow, and
          code examples, see the{' '}
          <a
            href='https://github.com/Mattilsynet/provetaking-labintegration'
            target='_blank'
            rel='noreferrer'
          >
            Lab Integration API Guide
          </a>
          {'. Full API reference: '}
          <a
            href={`${BASE_URL}/swagger-ui/index.html?urls.primaryName=Endpoints+for+lab+integration`}
            target='_blank'
            rel='noreferrer'
          >
            Swagger UI
          </a>
          {'.'}
        </p>
      </Mtds.Alert>
      <Mtds.Flex data-align={'center'} data-justify={'space-between'}>
        <Mtds.Togglegroup
          {...({ focusgroup: 'radiogroup' } as Record<string, string>)}
          aria-label='Status for analysekombinasjoner'
        >
          {items.map(([value, label]) => (
            <Mtds.Togglegroup.Item
              name='combinations-toggle'
              key={value}
              value={value}
              checked={toggleValue === value}
              onChange={() => {
                setToggleValue(value)
                setPagination({
                  pageIndex: 0,
                  pageSize: pagination.pageSize
                })
              }}
            >
              {label}
            </Mtds.Togglegroup.Item>
          ))}
        </Mtds.Togglegroup>
        <Mtds.Field
          as='input'
          label='Show codes'
          type='checkbox'
          role='switch'
          checked={showCodes}
          onChange={(event) => setShowCodes(event.target.checked)}
        />
      </Mtds.Flex>
      {selectedTableData.length ? (
        <CombinationsTable
          data={selectedTableData}
          showCodes={showCodes}
          pagination={pagination}
          setPagination={setPagination}
        />
      ) : (
        <EmptyState
          title={
            tableData.length
              ? 'No combinations match the selected status'
              : 'No combinations'
          }
          description={
            tableData.length
              ? 'Choose another status to see more combinations.'
              : 'There are no contract combinations to review yet.'
          }
        />
      )}
    </AppLayout>
  )
}
